import type { TopographyPoint } from "@features/Visualization/renderers/topographyContracts";
import type { EEGChannel } from "../services/EEGWaveformRenderer";

export type LiveTopographyAnchor = {
  key: string;
  label: string;
  x: number;
  y: number;
  z?: number;
};

/** Clinical sampling mode for the live scalp adjunct. */
export type LiveTopographyTimeMode = "instant" | "paused" | "unavailable";

export type LiveTopographyPreviewModel = {
  points: TopographyPoint[];
  minValue: number;
  maxValue: number;
  status: string;
  detail: string;
  mappedChannels: number;
  totalChannels: number;
  /** Cursor time the map is locked to (seconds into the recording). */
  sampleTimeSeconds: number | null;
  /**
   * Instant = voltage at the EEG cursor with light temporal smoothing.
   * Not a buffer-wide average (load buffer is sampling source only).
   */
  timeMode: LiveTopographyTimeMode;
  /** Half-window of temporal smoothing around the cursor, in milliseconds. */
  smoothingMs: number;
  /** Display unit for the diverging voltage color scale. */
  valueUnit: string;
  /** Loaded waveform buffer start used for sampling (not the map average window). */
  bufferStartSeconds: number | null;
  bufferEndSeconds: number | null;
};

export const DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN = 25;
/** Temporal half-window around the cursor when sampling live scalp voltages. */
export const LIVE_TOPOGRAPHY_SMOOTHING_SECONDS = 0.04;
export const LIVE_TOPOGRAPHY_VALUE_UNIT = "µV";
/** Compact dock adjunct size — keep readable labels without dominating the page. */
export const LIVE_SCALP_DOCK_MAP_WIDTH = 248;
export const LIVE_SCALP_DOCK_MAP_HEIGHT = 146;

export const LIVE_TOPOGRAPHY_COORDINATES: Record<
  string,
  Omit<LiveTopographyAnchor, "key" | "label">
> = {
  FP1: { x: -0.44, y: 0.94 },
  FP2: { x: 0.44, y: 0.94 },
  F7: { x: -0.9, y: 0.56 },
  F3: { x: -0.42, y: 0.56 },
  FZ: { x: 0, y: 0.6 },
  F4: { x: 0.42, y: 0.56 },
  F8: { x: 0.9, y: 0.56 },
  T3: { x: -0.98, y: 0.02 },
  T4: { x: 0.98, y: 0.02 },
  T5: { x: -0.86, y: -0.44 },
  T6: { x: 0.86, y: -0.44 },
  C3: { x: -0.46, y: 0.08 },
  CZ: { x: 0, y: 0.1 },
  C4: { x: 0.46, y: 0.08 },
  P3: { x: -0.42, y: -0.38 },
  PZ: { x: 0, y: -0.42 },
  P4: { x: 0.42, y: -0.38 },
  O1: { x: -0.34, y: -0.92 },
  OZ: { x: 0, y: -0.98 },
  O2: { x: 0.34, y: -0.92 },
  A1: { x: -0.96, y: -0.16, z: -0.22 },
  A2: { x: 0.96, y: -0.16, z: -0.22 },
};

export const LIVE_TOPOGRAPHY_ALIASES: Record<string, string> = {
  T7: "T3",
  T8: "T4",
  P7: "T5",
  P8: "T6",
  M1: "A1",
  M2: "A2",
};

export function liveTopographySmoothingMs(): number {
  return Math.round(LIVE_TOPOGRAPHY_SMOOTHING_SECONDS * 1000);
}

export function createEmptyLiveTopographyPreview(
  status: string,
  detail: string,
  totalChannels = 0,
  timeMode: LiveTopographyTimeMode = status.toLowerCase().includes("paused")
    ? "paused"
    : "unavailable",
): LiveTopographyPreviewModel {
  return {
    points: [],
    minValue: -DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN,
    maxValue: DEFAULT_LIVE_TOPOGRAPHY_ABS_DOMAIN,
    status,
    detail,
    mappedChannels: 0,
    totalChannels,
    sampleTimeSeconds: null,
    timeMode,
    smoothingMs: liveTopographySmoothingMs(),
    valueUnit: LIVE_TOPOGRAPHY_VALUE_UNIT,
    bufferStartSeconds: null,
    bufferEndSeconds: null,
  };
}

export function formatLiveTopographyScaleValue(value: number): string {
  const absolute = Math.abs(value);
  if (absolute >= 100) {
    return value.toFixed(0);
  }
  if (absolute >= 10) {
    return value.toFixed(1);
  }
  return value.toFixed(2);
}

/** Explicit time semantics for clinical review (instant ≠ buffer average). */
export function formatLiveTopographyTimeSemantics(
  preview: Pick<
    LiveTopographyPreviewModel,
    "timeMode" | "sampleTimeSeconds" | "smoothingMs" | "status"
  >,
): string {
  if (
    preview.timeMode === "instant" &&
    preview.sampleTimeSeconds != null &&
    Number.isFinite(preview.sampleTimeSeconds)
  ) {
    return `Instant @ ${preview.sampleTimeSeconds.toFixed(1)} s · ±${preview.smoothingMs} ms`;
  }

  return preview.status;
}

/** Explicit voltage color-scale legend (±µV). */
export function formatLiveTopographyScaleSemantics(
  preview: Pick<
    LiveTopographyPreviewModel,
    "minValue" | "maxValue" | "valueUnit"
  >,
): string {
  const unit = preview.valueUnit || LIVE_TOPOGRAPHY_VALUE_UNIT;
  return `${formatLiveTopographyScaleValue(preview.minValue)} … ${formatLiveTopographyScaleValue(preview.maxValue)} ${unit}`;
}

export function formatLiveTopographyBufferSemantics(
  preview: Pick<
    LiveTopographyPreviewModel,
    "bufferStartSeconds" | "bufferEndSeconds" | "mappedChannels" | "totalChannels"
  >,
): string | null {
  const mapped =
    preview.totalChannels > 0
      ? `${preview.mappedChannels}/${preview.totalChannels} mapped`
      : null;

  let buffer: string | null = null;
  if (
    preview.bufferStartSeconds != null &&
    Number.isFinite(preview.bufferStartSeconds)
  ) {
    buffer =
      preview.bufferEndSeconds != null &&
      Number.isFinite(preview.bufferEndSeconds)
        ? `Load ${preview.bufferStartSeconds.toFixed(1)}-${preview.bufferEndSeconds.toFixed(1)} s`
        : `Load from ${preview.bufferStartSeconds.toFixed(1)} s`;
  }

  if (mapped && buffer) {
    return `${mapped} · ${buffer}`;
  }
  return mapped ?? buffer;
}

export function canonicalizeLiveTopographyToken(token: string): string | null {
  const trimmed = token.trim().toUpperCase();
  if (!trimmed) {
    return null;
  }

  const canonical = LIVE_TOPOGRAPHY_ALIASES[trimmed] ?? trimmed;
  return Object.prototype.hasOwnProperty.call(
    LIVE_TOPOGRAPHY_COORDINATES,
    canonical,
  )
    ? canonical
    : null;
}

export function resolveLiveTopographyAnchor(
  channelName: string,
): LiveTopographyAnchor | null {
  const tokens = channelName
    .toUpperCase()
    .replace(/\bEEG\b/g, " ")
    .replace(/\b(REF|LE|RE|AVG|AV|AR|AAR|CAR|EKG|ECG|EMG)\b/g, " ")
    .replace(/[_/]+/g, " ")
    .replace(/[^A-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .reduce<string[]>((accumulator, token) => {
      const canonical = canonicalizeLiveTopographyToken(token);
      if (!canonical || accumulator.includes(canonical)) {
        return accumulator;
      }
      accumulator.push(canonical);
      return accumulator;
    }, [])
    .slice(0, 2);

  if (tokens.length === 0) {
    return null;
  }

  const coordinates = tokens.map((token) => LIVE_TOPOGRAPHY_COORDINATES[token]);
  const label = tokens.join("-");
  const zValues = coordinates
    .map((coordinate) => coordinate.z)
    .filter((value): value is number => typeof value === "number");

  return {
    key: label,
    label,
    x:
      coordinates.reduce((sum, coordinate) => sum + coordinate.x, 0) /
      coordinates.length,
    y:
      coordinates.reduce((sum, coordinate) => sum + coordinate.y, 0) /
      coordinates.length,
    z:
      zValues.length > 0
        ? zValues.reduce((sum, value) => sum + value, 0) / zValues.length
        : undefined,
  };
}

export function sampleWeightedSeries(
  series: ArrayLike<number>,
  centerIndex: number,
  radius: number,
): number | null {
  if (series.length === 0) {
    return null;
  }

  const clampedCenter = Math.max(0, Math.min(series.length - 1, centerIndex));
  const startIndex = Math.max(0, clampedCenter - radius);
  const endIndex = Math.min(series.length - 1, clampedCenter + radius);
  let weightedSum = 0;
  let totalWeight = 0;

  for (let index = startIndex; index <= endIndex; index += 1) {
    const distance = Math.abs(index - clampedCenter);
    const weight = radius === 0 ? 1 : 1 - distance / (radius + 1);
    weightedSum += Number(series[index]) * weight;
    totalWeight += weight;
  }

  return totalWeight > 0
    ? weightedSum / totalWeight
    : Number(series[clampedCenter]);
}

export function sampleLiveTopographyValue(
  channel: EEGChannel,
  sampleTimeSeconds: number,
  windowStartSeconds: number,
): number | null {
  if (!Number.isFinite(channel.sampleRate) || channel.sampleRate <= 0) {
    return null;
  }

  const centerIndex = Math.round(
    (sampleTimeSeconds - windowStartSeconds) * channel.sampleRate,
  );
  const smoothingRadius = Math.max(
    1,
    Math.min(
      16,
      Math.round(channel.sampleRate * LIVE_TOPOGRAPHY_SMOOTHING_SECONDS),
    ),
  );

  return sampleWeightedSeries(channel.data, centerIndex, smoothingRadius);
}
